import { Icon } from "@mdi/react";
import { memo } from "react";

import {
  CloseOutlined,
  LockOpenOutlined,
  LockOutlined,
  SyncOutlined
} from "@material-ui/icons";
import { mdiShieldSync, mdiShieldSyncOutline } from "@mdi/js";
import { Button, Checkbox, Divider, Popover, Select, Switch, Tag, Tooltip } from "antd";
import styled from "styled-components";

import { SyncFill } from "components/icons";

import {
  ClientSyncViewModelProps,
  MessageKind,
  PopoverMessage,
  SyncType,
  useClientSyncViewModel
} from "./hooks/useClientSyncViewModel";

const { Option } = Select;

// ─── Types ────────────────────────────────────────────────────────────────────

interface SyncButtonProps {
  $synced: boolean;
  $unavailable?: boolean;
  $hasError?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const Sync = (props: ClientSyncViewModelProps) => {
  const vm = useClientSyncViewModel(props);
  const hasSyncError =
    vm.globalMessage?.kind === "error" ||
    vm.syncMessages.production?.kind === "error" ||
    vm.syncMessages.forecast?.kind === "error" ||
    vm.syncMessages.columns?.kind === "error" ||
    vm.performanceMessage?.kind === "error";

  return (
    <RootContainer>
      <Popover
        trigger="click"
        content={
          <OptionContainer>
            {vm.isSyncServiceUnavailable && (
              <ServiceUnavailableBanner>
                <span>{vm.syncServiceErrorMessage}</span>
                <RetryButton
                  onClick={() => vm.refetchSchemas()}
                  disabled={vm.isLoadingSchemas}>
                  {vm.isLoadingSchemas ? "Checking..." : "Retry"}
                </RetryButton>
              </ServiceUnavailableBanner>
            )}

            {vm.globalMessage && (
              <InlineMessage $kind={vm.globalMessage.kind}>
                <span>{vm.globalMessage.text}</span>
                {(vm.globalMessage.kind === "error" ||
                  vm.globalMessage.kind === "warn") && (
                  <InlineMessageDismiss
                    onClick={vm.dismissGlobalMessage}
                    aria-label="Dismiss">
                    ✕
                  </InlineMessageDismiss>
                )}
              </InlineMessage>
            )}

            <OptionItem>
              <Tag color={vm.currentSchema ? "green" : "red"}>
                {vm.currentSchema ? vm.currentSchema : "No Schema Selected"}
              </Tag>
              <Tag color={vm.isSynced ? "green" : "orange"}>
                {vm.isSynced ? "Synced" : "Not Synced"}
              </Tag>
              <Tag color={vm.pdenStatus.lastUpdated ? "green" : "red"}>Production</Tag>
              <Tag color={vm.fcstStatus.lastUpdated ? "green" : "red"}>Forecast</Tag>
              <Tag color={vm.columnStatus.lastUpdated ? "green" : "red"}>Column</Tag>
            </OptionItem>

            <Divider />

            <OptionItem>
              <SchemaWrapper>
                <div>Database</div>
                <Select
                  value={vm.currentSchema || undefined}
                  options={vm.schemas}
                  data-testid="sync-job-select"
                  popupClassName="modal-select"
                  onChange={vm.handleSchemaChange}
                  disabled={vm.isSchemaDisabled}
                  loading={vm.isLoadingSchemas}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toString()
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  showSearch
                />
                <Switch
                  data-testid="sync-lock-switch"
                  title="Prevent selected job from changing while sync is active."
                  checkedChildren={
                    <LockIconWrapper>
                      <LockOutlined />
                    </LockIconWrapper>
                  }
                  unCheckedChildren={
                    <LockIconWrapper>
                      <LockOpenOutlined />
                    </LockIconWrapper>
                  }
                  checked={vm.isJobLocked}
                  onChange={vm.setIsJobLocked}
                />
              </SchemaWrapper>
            </OptionItem>

            <Divider />

            {vm.currentSchema && (
              <>
                <OptionItem>
                  <Checkbox
                    disabled={!vm.pdenStatus.lastUpdated || !vm.isSynced}
                    checked={vm.usePdenFromSyncDb}
                    onChange={(e) => vm.setUsePdenFromSyncDb(e.target.checked)}>
                    Use Production from Sync DB
                  </Checkbox>
                </OptionItem>

                <OptionItem>
                  <Checkbox
                    disabled={!vm.fcstStatus.lastUpdated || !vm.isSynced}
                    checked={vm.useFcstFromSyncDb}
                    onChange={(e) => vm.setUseFcstFromSyncDb(e.target.checked)}>
                    Use Forecast from Sync DB
                  </Checkbox>
                  <Select value={vm.forecastRescat} onChange={vm.setForecastRescat}>
                    <Option value="1111">PDP</Option>
                    <Option value="1211">PD</Option>
                    <Option value="1311">TP</Option>
                    <Option value="2111">P+PDP</Option>
                    <Option value="2211">P+PD</Option>
                    <Option value="2311">TPP</Option>
                    <Option value="3111">P+P+PDP</Option>
                    <Option value="3211">P+P+PD</Option>
                    <Option value="3311">TPPP</Option>
                  </Select>
                </OptionItem>

                <Divider />

                <OptionItemWithStatus>
                  <div>
                    <Tag color={vm.pdenStatus.lastUpdated ? "green" : "red"}>
                      {vm.pdenStatus.lastUpdated
                        ? "Production: " + vm.toDateTime(vm.pdenStatus.lastUpdated)
                        : "Warning: No Production Found"}
                    </Tag>
                  </div>
                  <CancelableButton
                    syncType="production"
                    isLoading={vm.loadingStatus.pden}
                    isHovered={vm.hoveredSyncType === "production"}
                    onHoverChange={(hovered) =>
                      vm.setHoveredSyncType(hovered ? "production" : null)
                    }
                    onSync={() => vm.startSync("production")}
                    onCancel={() => vm.cancelSync("production")}
                  />
                </OptionItemWithStatus>
                {vm.syncMessages.production && (
                  <SyncMessageRow
                    message={vm.syncMessages.production}
                    onDismiss={() => vm.dismissSyncMessage("production")}
                  />
                )}

                <OptionItemWithStatus>
                  <div>
                    <Tag color={vm.fcstStatus.lastUpdated ? "green" : "red"}>
                      {vm.fcstStatus.lastUpdated
                        ? "Forecast: " + vm.toDateTime(vm.fcstStatus.lastUpdated)
                        : "Warning: No Forecast Found"}
                    </Tag>
                  </div>
                  <CancelableButton
                    syncType="forecast"
                    isLoading={vm.loadingStatus.fcst}
                    isHovered={vm.hoveredSyncType === "forecast"}
                    onHoverChange={(hovered) =>
                      vm.setHoveredSyncType(hovered ? "forecast" : null)
                    }
                    onSync={() => vm.startSync("forecast")}
                    onCancel={() => vm.cancelSync("forecast")}
                  />
                </OptionItemWithStatus>
                {vm.syncMessages.forecast && (
                  <SyncMessageRow
                    message={vm.syncMessages.forecast}
                    onDismiss={() => vm.dismissSyncMessage("forecast")}
                  />
                )}

                <OptionItemWithStatus>
                  <div>
                    <Tag color={vm.columnStatus.lastUpdated ? "green" : "red"}>
                      {vm.columnStatus.lastUpdated
                        ? "Columns: " + vm.toDateTime(vm.columnStatus.lastUpdated)
                        : "Warning: No Columns Found"}
                    </Tag>
                  </div>
                  <CancelableButton
                    syncType="columns"
                    isLoading={vm.loadingStatus.column}
                    isHovered={vm.hoveredSyncType === "columns"}
                    onHoverChange={(hovered) =>
                      vm.setHoveredSyncType(hovered ? "columns" : null)
                    }
                    onSync={() => vm.startSync("columns")}
                    onCancel={() => vm.cancelSync("columns")}
                  />
                </OptionItemWithStatus>
                {vm.syncMessages.columns && (
                  <SyncMessageRow
                    message={vm.syncMessages.columns}
                    onDismiss={() => vm.dismissSyncMessage("columns")}
                  />
                )}

                <OptionItemWithStatus>
                  <div>
                    <Tag color={vm.performanceStatus.lastUpdated ? "green" : "red"}>
                      {vm.performanceStatus.lastUpdated
                        ? "Performance: " +
                          vm.toDateTime(vm.performanceStatus.lastUpdated)
                        : "Warning: No Performance Found"}
                    </Tag>
                  </div>
                  <Tooltip title="Performance is automatically refreshed after syncing production or forecast data">
                    <Button
                      shape="circle"
                      type="primary"
                      disabled
                      loading={vm.loadingStatus.performance}
                      icon={<SyncOutlined />}
                    />
                  </Tooltip>
                </OptionItemWithStatus>
                {vm.performanceMessage && (
                  <SyncMessageRow
                    message={vm.performanceMessage}
                    onDismiss={vm.dismissPerformanceMessage}
                  />
                )}

                <Divider />
              </>
            )}

            <OptionItem>
              <SyncButton onClick={vm.updateSyncState} $synced={vm.isSynced}>
                Sync is {vm.isSynced ? "ON" : "OFF"}
              </SyncButton>

              <Tooltip title="When activated, pressing the space bar will sync production and forecast for only the active wells.">
                <Switch
                  data-testid="super-sync-switch"
                  checkedChildren={
                    <LockIconWrapper>
                      <Icon path={mdiShieldSync} size={1} />
                    </LockIconWrapper>
                  }
                  unCheckedChildren={
                    <LockIconWrapper>
                      <Icon path={mdiShieldSyncOutline} size={1} />
                    </LockIconWrapper>
                  }
                  checked={vm.useSuperSync}
                  onChange={vm.setUseSuperSync}
                />
              </Tooltip>
            </OptionItem>
          </OptionContainer>
        }>
        <StyledButton
          $synced={vm.isSynced}
          $unavailable={vm.isSyncServiceUnavailable}
          $hasError={hasSyncError}
          icon={<SyncFill className="source-icon" />}
          data-testid="sync-popover-trigger">
          {vm.isSynced && !vm.isSyncServiceUnavailable && (
            <EllipsisText>{vm.currentSchema}</EllipsisText>
          )}
        </StyledButton>
      </Popover>
    </RootContainer>
  );
};

export default memo(Sync);

// ─── SyncMessageRow ──────────────────────────────────────────────────────────

interface SyncMessageRowProps {
  message: PopoverMessage;
  onDismiss: () => void;
}

const SyncMessageRow = ({ message, onDismiss }: SyncMessageRowProps) => (
  <InlineMessage $kind={message.kind}>
    <span>{message.text}</span>
    {message.kind !== "info" && (
      <InlineMessageDismiss onClick={onDismiss} aria-label="Dismiss">
        ✕
      </InlineMessageDismiss>
    )}
  </InlineMessage>
);

// ─── CancelableButton ────────────────────────────────────────────────────────

interface CancelableButtonProps {
  syncType: SyncType;
  isLoading: boolean;
  isHovered: boolean;
  onHoverChange: (hovered: boolean) => void;
  onSync: () => void;
  onCancel: () => void;
}

const CancelableButton = memo(function CancelableButton({
  isLoading,
  isHovered,
  onHoverChange,
  onSync,
  onCancel
}: CancelableButtonProps) {
  const showCancel = isLoading && isHovered;

  return (
    <Button
      shape="circle"
      type="primary"
      danger={showCancel}
      loading={isLoading && !isHovered}
      icon={showCancel ? <CloseOutlined /> : <SyncOutlined />}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      onClick={isLoading ? onCancel : onSync}
    />
  );
});

// ─── Styled components ────────────────────────────────────────────────────────

const RootContainer = styled.div`
  .rotate {
    background-color: transparent;
    outline: 2px dashed;
    transform: rotate(180deg);
  }
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  .ant-divider-horizontal {
    margin: 5px 0 !important;
  }
`;

const OptionItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 5px;

  .ant-slider {
    width: 80px;
  }

  .ant-select {
    width: 100px;
  }

  .ant-input {
    height: 25px;
    max-width: 80px;
  }

  .ant-btn {
    border-radius: 5px;
  }

  .ant-btn-circle {
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-btn:hover {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  padding: 2px 0;
`;

const SchemaWrapper = styled.div`
  min-width: 200px;
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;

  .ant-select {
    min-width: 200px;
  }
`;

const OptionItemWithStatus = styled(OptionItem)`
  justify-content: space-between;
  align-items: center;

  .ant-btn {
    align-items: center;
  }
`;

const SyncButton = styled(Button)`
  color: white;
  background-color: ${(props: SyncButtonProps) =>
    props.$synced ? "var(--color-primary)" : "var(--color-text)"};
`;

const StyledButton = styled(Button)<SyncButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => {
    if (props.$hasError) return "var(--color-danger, #ff4d4f)";
    if (props.$unavailable) return "var(--color-danger, #ff4d4f)";
    if (props.$synced) return "var(--color-primary)";
    return "var(--color-icon)";
  }};

  max-width: 180px;

  .source-icon {
    font-size: 16px;
  }
`;

const KIND_COLORS: Record<MessageKind, { bg: string; border: string; text: string }> = {
  success: { bg: "#f6ffed", border: "#b7eb8f", text: "#389e0d" },
  error: { bg: "#fff2f0", border: "#ffccc7", text: "#ff4d4f" },
  warn: { bg: "#fffbe6", border: "#ffe58f", text: "#d48806" },
  info: { bg: "#e6f4ff", border: "#91caff", text: "#0958d9" }
};

const InlineMessage = styled.div<{ $kind: MessageKind }>`
  background-color: ${(p) => KIND_COLORS[p.$kind].bg};
  border: 1px solid ${(p) => KIND_COLORS[p.$kind].border};
  border-radius: 4px;
  color: ${(p) => KIND_COLORS[p.$kind].text};
  font-size: 11px;
  padding: 4px 8px;
  margin: 2px 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;

  span {
    flex: 1;
    line-height: 1.4;
  }
`;

const InlineMessageDismiss = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 10px;
  line-height: 1;
  opacity: 0.7;
  padding: 0;
  flex-shrink: 0;
  margin-top: 1px;

  &:hover {
    opacity: 1;
  }
`;

const ServiceUnavailableBanner = styled.div`
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  color: #ff4d4f;
  font-size: 12px;
  margin-bottom: 6px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const RetryButton = styled.button`
  background: none;
  border: 1px solid #ff4d4f;
  border-radius: 3px;
  color: #ff4d4f;
  cursor: pointer;
  font-size: 11px;
  padding: 1px 7px;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background-color: #ff4d4f;
    color: #fff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LockIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EllipsisText = styled.span`
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
